import {
  aportado,
  aportadoTecnico,
  grupoResuelto,
} from "../../../utils/documentoAportado";

/**
 * Los pasos del wizard de postulacion, en una sola pagina.
 *
 * Antes cada paso era una pagina con su propia ruta, y lo que se arrastraba
 * entre ellas vivia en localStorage. Aqui los pasos son datos: la vista los
 * recorre para pintar el stepper y para decidir que contenido mostrar.
 */

export interface PasoPostulacion {
  clave: string;
  /** El numero que ve la persona, empezando en 1. */
  numero: number;
  titulo: string;
  /** Una linea de ayuda bajo el titulo del paso. */
  descripcion: string;
}

export const PASOS: PasoPostulacion[] = [
  {
    clave: "perfil",
    numero: 1,
    titulo: "Perfil y datos personales",
    descripcion: "Con que perfil te presentas y tus datos de contacto",
  },
  {
    clave: "propuesta",
    numero: 2,
    titulo: "Categoría y documentos",
    descripcion: "La categoría a la que te presentas y los documentos que exige",
  },
  {
    clave: "cierre",
    numero: 3,
    titulo: "Nombre del proyecto y enlaces",
    descripcion: "El nombre de tu proyecto y los enlaces que quieras adjuntar",
  },
];

export const PRIMER_PASO = 0;
export const ULTIMO_PASO = PASOS.length - 1;

/** Deja el indice dentro del rango, para que nada pueda salirse de los pasos. */
export const acotarPaso = (indice: number): number => {
  if (!Number.isFinite(indice)) return PRIMER_PASO;
  return Math.min(Math.max(Math.trunc(indice), PRIMER_PASO), ULTIMO_PASO);
};

/** El indice del paso con esa clave, o el primero si la clave no existe. */
export const indiceDePaso = (clave?: string | null): number => {
  const i = PASOS.findIndex((paso) => paso.clave === clave);
  return i < 0 ? PRIMER_PASO : i;
};

export const esUltimoPaso = (indice: number): boolean =>
  acotarPaso(indice) === ULTIMO_PASO;

export const esPrimerPaso = (indice: number): boolean =>
  acotarPaso(indice) === PRIMER_PASO;

/**
 * Los tipos de participante que la convocatoria tiene habilitados.
 *
 * La convocatoria los trae como { value, text }. Si no define ninguno, no se
 * ofrece ninguno: es preferible que no se pueda elegir a que alguien se
 * postule con un perfil que la convocatoria no admite.
 */
export const perfilesHabilitados = (convocatoria: any): any[] => {
  const tipos = convocatoria?.tipo_participante;
  return Array.isArray(tipos) ? tipos : [];
};

/**
 * Como se guarda el tipo de participante para el resto del flujo.
 *
 * Al registrarse los tipos van de 1 a 3, pero los documentos los evaluan de
 * 0 a 2, asi que se guarda restando uno. La conversion estaba suelta dentro
 * de useSeleccionarRoles; aqui queda con nombre y con pruebas.
 */
export const tipoParaDocumentos = (tipoParticipante: any): string =>
  `${Number(tipoParticipante) - 1}`;

/**
 * Que recoge cada paso, para poder decir no solo si esta completo sino que le
 * falta. El modal de "continua con tu postulacion" los muestra uno por uno.
 */
const PARTES: {
  [clave: string]: { etiqueta: string; campo: string; aplicaSi?: string }[];
} = {
  perfil: [
    { etiqueta: "Tipo de participante", campo: "tipo" },
    { etiqueta: "Datos personales", campo: "personales" },
  ],
  propuesta: [
    { etiqueta: "Categoría", campo: "categorias" },
    {
      etiqueta: "Documentos administrativos",
      campo: "administrativos",
      aplicaSi: "pideAdministrativos",
    },
    { etiqueta: "Documentos técnicos", campo: "tecnicos", aplicaSi: "pideTecnicos" },
  ],
  cierre: [
    { etiqueta: "Nombre del proyecto", campo: "nombre" },
    { etiqueta: "Enlaces", campo: "links" },
  ],
};

export interface PartePaso {
  etiqueta: string;
  completo: boolean;
}

export interface PasoConEstado extends PasoPostulacion {
  completo: boolean;
  /** Lo que recoge el paso, cada cosa con su estado */
  partes: PartePaso[];
}

/**
 * Como se ve cada paso en el modal de "continua con tu postulacion".
 *
 * El modal listaba los cinco pasos de las paginas viejas; ahora son tres, y
 * cada uno junta varias cosas. Un paso esta completo cuando lo estan todas
 * sus partes: no hay una segunda regla que pueda discrepar de lo que el modal
 * muestra renglon por renglon.
 */
export const estadoDeLosPasos = (pasos: any): PasoConEstado[] =>
  PASOS.map((paso) => {
    const partes: PartePaso[] = (PARTES[paso.clave] ?? [])
      // Lo que la convocatoria no pide no se muestra. Ponerle un check a
      // un grupo vacio decia que estaba hecho algo que nunca hubo que
      // hacer. Solo se oculta con un no rotundo: sin el dato se muestra.
      .filter((parte) => !parte.aplicaSi || pasos?.[parte.aplicaSi] !== false)
      .map((parte) => ({
        etiqueta: parte.etiqueta,
        completo: Boolean(pasos?.[parte.campo]),
      }));

    return { ...paso, partes, completo: partes.every((parte) => parte.completo) };
  });

/** La direccion del wizard abierto en ese paso. */
export const rutaDelPaso = (clave: string): string =>
  `/Usuario/postulacion?paso=${clave}`;

/**
 * Que partes tiene completas una postulacion, segun lo que hay guardado.
 *
 * Es la regla que ya usaba "Mis proyectos" para el modal de continuar, suelta
 * dentro de la vista. Aqui queda con nombre y con pruebas, para que el
 * recuento previo a inscribir y ese modal no puedan discrepar.
 *
 * La categoria vacia cuenta como incompleta: antes `!= null` la daba por
 * puesta, de modo que el modal decia "Completado" sobre algo que despues
 * impedia inscribirse.
 */
export const pasosCompletados = (postulacion: any) => {
  const lista = (valor: any): any[] => (Array.isArray(valor) ? valor : []);
  const categoria = postulacion?.categoria_linea_convocatoria;

  const tipo = postulacion?.tipo_participante;

  return {
    tipo: tipo !== null && tipo !== undefined && tipo !== "",
    // Para postularse hay que estar registrado, asi que los datos personales
    // existen siempre; lo que les falte se advierte aparte.
    personales: true,
    categorias: categoria !== null && categoria !== undefined && categoria !== "",
    // Si la convocatoria no pide un grupo, su renglon no se muestra; y los
    // formatos que se diligencian en linea cuentan aunque no suban archivo.
    pideAdministrativos: lista(postulacion?.documentos_administrativos).length > 0,
    pideTecnicos: lista(postulacion?.documentos_tecnicos).length > 0,
    administrativos: grupoResuelto(postulacion?.documentos_administrativos, aportado),
    tecnicos: grupoResuelto(postulacion?.documentos_tecnicos, aportadoTecnico),
    nombre: String(postulacion?.nombre_propuesta ?? "").trim() !== "",
    links: lista(postulacion?.links).length > 0,
  };
};
